import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PopupContext } from 'components';
import { RESOURCE_LIMIT_EXCEEDED_MESSAGE } from 'constants/errorMessages';
import { REGION_SUBSCRIPTION_PLANS } from 'constants/routes';
import { useContext } from 'react';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { openUpgradeTierModal } from 'utils/handleTierExceededError';
import EVENTS_KEYS from '../events.keys';
import EVENTS_MUTATIONS from '../events.mutations';
import EVENTS_QUERIES from '../events.queries';

export const useFetchEvents = ({ page, pageSize, search, ordering, filters } = {}) => {
  const hasPaginationParams = page || pageSize || search || ordering || filters;
  const queryParams = { page, pageSize, search, ordering, filters };

  return useQuery({
    queryKey: hasPaginationParams
      ? EVENTS_KEYS.EVENTS_PAGINATED(queryParams)
      : EVENTS_KEYS.EVENTS(),
    queryFn: () => EVENTS_QUERIES.FETCH_EVENTS(queryParams),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const useFetchEvent = (uid) =>
  useQuery({
    queryKey: EVENTS_KEYS.EVENT(uid),
    queryFn: () => EVENTS_QUERIES.FETCH_EVENT(uid),
    enabled: !!uid,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchEventsUsage = (regionUid) =>
  useQuery({
    queryKey: EVENTS_KEYS.EVENT(regionUid),
    queryFn: () => EVENTS_QUERIES.FETCH_EVENTS_USAGE(regionUid),
    enabled: !!regionUid,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useCreateEvent = () => {
  const queryClient = useQueryClient();
  const selectedRegionUid = useSelectedProfileStore((state) => state.selectedRegionUid);
  const { closePopup } = useContext(PopupContext);

  return useMutation({
    mutationFn: EVENTS_MUTATIONS.CREATE_EVENT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEYS.EVENTS() });
      closePopup?.();
    },
    onError: (error) => {
      if (error.message === RESOURCE_LIMIT_EXCEEDED_MESSAGE) {
        closePopup?.();

        const upgradeUrl = REGION_SUBSCRIPTION_PLANS.replace(':id', selectedRegionUid);

        openUpgradeTierModal({
          resourceType: 'events',
          upgradeUrl,
        });
      }
    },
  });
};

export const useUpdateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: EVENTS_MUTATIONS.UPDATE_EVENT,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEYS.EVENTS() });
      queryClient.invalidateQueries({ queryKey: EVENTS_KEYS.EVENT(variables.uid) });
    },
  });
};

export const useDeleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: EVENTS_MUTATIONS.DELETE_EVENT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEYS.EVENTS() });
    },
  });
};
